import { Outlet } from 'react-router-dom';
import { Fingerprint, KeyRound, ShieldCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export function AuthLayout() {
  const { t } = useTranslation();

  return (
    <main className="grid min-h-screen bg-card lg:grid-cols-[minmax(420px,0.9fr)_1.1fr]">
      <section className="relative hidden overflow-hidden bg-sidebar p-10 text-white lg:flex lg:flex-col xl:p-14">
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage: 'linear-gradient(hsl(var(--sidebar-foreground) / .06) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--sidebar-foreground) / .06) 1px, transparent 1px)',
            backgroundSize: '44px 44px',
            maskImage: 'linear-gradient(to bottom, black, transparent 85%)',
          }}
        />

        <div className="relative z-10 flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-accent/10 ring-1 ring-accent/20">
            <img src="/logotipo.png" alt="" className="h-9 w-9 object-contain" />
          </div>
          <div>
            <p className="text-lg font-semibold tracking-[-0.02em]">OneckPass</p>
            <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-sidebar-muted">Zero-knowledge security</p>
          </div>
        </div>

        <div className="relative z-10 my-auto max-w-lg py-16">
          <p className="mb-5 font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-accent">{t('shell.privateByDesign')}</p>
          <h1 className="text-4xl font-semibold leading-[1.08] tracking-[-0.045em] xl:text-5xl">
            {t('shell.authHeadline')}
          </h1>
          <p className="mt-5 max-w-md text-base leading-7 text-sidebar-muted">
            {t('shell.authDescription')}
          </p>

          <div className="mt-10 grid grid-cols-3 gap-3">
            {[
              { icon: Fingerprint, label: t('shell.privateAccess') },
              { icon: KeyRound, label: t('shell.e2eEncryption') },
              { icon: ShieldCheck, label: 'Zero knowledge' },
            ].map(({ icon: Icon, label }) => (
              <div key={label} className="rounded-2xl border border-white/10 bg-white/[0.055] p-4">
                <Icon className="mb-5 h-5 w-5 text-accent" strokeWidth={1.8} />
                <p className="text-xs font-medium leading-5 text-sidebar-foreground">{label}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10 flex items-center justify-between font-mono text-[9px] uppercase tracking-[0.14em] text-sidebar-muted">
          <span>AES-256-GCM</span>
          <span>{t('shell.protectedLocally')}</span>
        </div>
      </section>

      <section className="relative flex min-h-screen items-center justify-center overflow-hidden bg-background px-4 py-10 sm:px-8">
        <div aria-hidden="true" className="absolute -right-24 -top-24 h-80 w-80 rounded-full bg-primary/10 blur-3xl" />
        <div className="relative z-10 w-full max-w-[430px]">
          <div className="mb-8 flex items-center justify-center gap-3 lg:hidden">
            <img src="/logotipo.png" alt="Oneck Pass" className="h-11 w-11 object-contain" />
            <span className="text-xl font-semibold tracking-[-0.025em]">OneckPass</span>
          </div>
          <div className="rounded-3xl border border-border/90 bg-card p-6 shadow-lift sm:p-8">
            <Outlet />
          </div>
          <p className="mt-6 text-center font-mono text-[9px] uppercase tracking-[0.14em] text-muted-foreground">
            Zero-knowledge · End-to-end encrypted
          </p>
        </div>
      </section>
    </main>
  );
}
